import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { getIdTokenResult } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../config/firebase";
import { getStaffProvisioningEnvironment } from "../config/staffProvisioningEnvironment";
import { canAccessAdminPortal } from "../utils/adminRoles";
import "./styles/ReceiptPrinterControls.css";

const PRINTER_MODELS = [
	{ value: "V-WRP2", label: "Volcora V-WRP2" },
	{ value: "TSP100IV", label: "Star TSP143IV-UE" },
];
const validModel = (model) => PRINTER_MODELS.some((option) => option.value === model);
const modelLabel = (model) => PRINTER_MODELS.find((option) => option.value === model)?.label;
const privateLan = (value) => {
	if (typeof value !== "string") return false;
	const parts = value.split(".");
	if (parts.length !== 4 || !parts.every((part) => /^(0|[1-9][0-9]{0,2})$/.test(part) && Number(part) <= 255)) return false;
	const [a, b] = parts.map(Number);
	return a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168);
};
const validPrinter = (printer) => printer && typeof printer === "object" &&
	printer.interfaceType === "Lan" && validModel(printer.model) && privateLan(printer.identifier);
const nullableString = (value) => value === null || typeof value === "string";
const validSnapshot = (value, restaurantId) => value?.restaurantId === restaurantId &&
	((typeof value.revision === "string" && value.revision.length > 0) || (Number.isSafeInteger(value.revision) && value.revision >= 0)) &&
	(value.receiptPrinter === null || validPrinter(value.receiptPrinter)) &&
	typeof value.receiptPrintingEnabled === "boolean" && typeof value.drawerCommandsEnabled === "boolean" &&
	(value.automaticPickupPrintingEnabled === undefined || typeof value.automaticPickupPrintingEnabled === "boolean") &&
	value.connectionStatus === "not_checked" && Array.isArray(value.devices) &&
	value.devices.every((device) => device && typeof device.deviceId === "string" && device.deviceId.length > 0 &&
		typeof device.label === "string" && typeof device.revoked === "boolean" && typeof device.eligible === "boolean" &&
		typeof device.configEnabled === "boolean" && nullableString(device.drawerPrinterId) && nullableString(device.drawerIdentifier)) &&
	new Set(value.devices.map((device) => device.deviceId)).size === value.devices.length &&
	Array.isArray(value.recentActivity) && value.recentActivity.every((row) => row && typeof row.id === "string" &&
		typeof row.kind === "string" && typeof row.state === "string" && nullableString(row.deviceId));
const canAssign = (device) => device.eligible && !device.revoked;
const enabledDeviceIds = (snapshot) => snapshot.devices.filter((device) => device.configEnabled).map((device) => device.deviceId);
const assignedDeviceIds = (snapshot) => snapshot.devices.filter((device) => device.configEnabled && canAssign(device)).map((device) => device.deviceId);
const sameDevices = (actual, expected) => actual.length === expected.length && actual.every((id) => expected.includes(id));
const stateLabel = (value) => value.replace(/_/g, " ");
const activityDate = (value) => {
	if (typeof value !== "string" && typeof value !== "number") return null;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};
const setupPreconditions = new Set([
	"Pause automatic pickup printing before enabling printing or changing the printer target.",
	"Resolve pending or uncertain print jobs before enabling printing or changing the printer target.",
	"Resolve the durable pickup print backlog before enabling printing or changing the printer target.",
	"Pause automatic pickup printing before enabling printing or changing the printer IP.",
	"Resolve pending or uncertain print jobs before enabling printing or changing the printer IP.",
	"Resolve the durable pickup print backlog before enabling printing or changing the printer IP.",
	"Resolve the current drawer command before changing its association or target.",
	"Observe and reconcile the current drawer command before changing printer setup.",
	"Every selected device must have valid, unrevoked enrollment for this restaurant and account.",
	"Stored receipt printer requires support review.",
	"Stored printer activation requires support review.",
	"Stored drawer association requires support review.",
	"Stored drawer target requires support review.",
	"Stored print activity requires support review.",
	"Stored drawer activity requires support review.",
	"Current drawer state requires support review.",
	"Drawer resolution requires support review.",
	"Printer setup diagnostic indexes are required.",
]);
const errorMessage = (error, saving) => {
	const code = String(error?.code || "").replace(/^functions\//, "");
	if (code === "aborted") return "Configuration conflict: the saved setup changed. Refresh and review before saving again.";
	if (code === "permission-denied" || code === "unauthenticated") return "Scerv admin access is required. Sign in with an authorized support account.";
	if (code === "invalid-argument") return "Setup rejected. Check the printer model, private LAN IPv4 address, devices and support reason. Refresh before saving again.";
	if (code === "not-found") return "Restaurant or setup endpoint not found. Deployment availability is not verified.";
	if (code === "failed-precondition") return setupPreconditions.has(error.message) ? `${error.message} Refresh before saving again.` :
		"Setup is blocked by server policy. Refresh before continuing.";
	return saving ? "Save outcome unconfirmed. The setup may have been saved. Refresh before another save." :
		"Could not load a valid saved printer setup. Refresh before saving.";
};

function PrinterForm({ restaurantId, user, environment, onSaved }) {
	const [snapshot, setSnapshot] = useState(null);
	const [model, setModel] = useState("V-WRP2");
	const [identifier, setIdentifier] = useState("");
	const [receiptPrintingEnabled, setReceiptPrintingEnabled] = useState(false);
	const [drawerCommandsEnabled, setDrawerCommandsEnabled] = useState(false);
	const [deviceIds, setDeviceIds] = useState([]);
	const [reason, setReason] = useState("");
	const [busy, setBusy] = useState(false);
	const [blocked, setBlocked] = useState(true);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const pending = useRef(false);
	const loadedSnapshot = useRef(null);
	const generation = useRef({ value: 0 });
	const alive = useRef(false);
	const serviceApp = useRef(functions.app).current;
	const sameScope = useCallback(() => getStaffProvisioningEnvironment() === environment &&
		functions.app === serviceApp && auth.currentUser === user, [environment, serviceApp, user]);
	const isCurrent = useCallback((ticket) => alive.current && generation.current.value === ticket && sameScope(), [sameScope]);
	const authorize = useCallback(async (ticket) => {
		if (!isCurrent(ticket)) return false;
		const token = await getIdTokenResult(user);
		if (!isCurrent(ticket)) return false;
		const role = String(token.claims.role || "");
		// Portal aliases are shared; whitespace must still match the callable's policy.
		if (!canAccessAdminPortal(role) || role !== role.trim()) {
			throw Object.assign(new Error("Denied"), { code: "permission-denied" });
		}
		return true;
	}, [isCurrent, user]);
	const applySnapshot = useCallback((saved, preserveDraft = false) => {
		// Keep the last valid baseline even when a refresh fails and hides saved details.
		const previous = loadedSnapshot.current;
		const merge = (current, before, after, equal = Object.is) =>
			preserveDraft && previous && !equal(current, before) ? current : after;
		loadedSnapshot.current = saved;
		setSnapshot(saved);
		setModel((current) => merge(current, previous?.receiptPrinter?.model || "V-WRP2", saved.receiptPrinter?.model || "V-WRP2"));
		setIdentifier((current) => merge(current, previous?.receiptPrinter?.identifier || "", saved.receiptPrinter?.identifier || ""));
		setReceiptPrintingEnabled((current) => merge(current, previous?.receiptPrintingEnabled, saved.receiptPrintingEnabled));
		setDrawerCommandsEnabled((current) => merge(current, previous?.drawerCommandsEnabled, saved.drawerCommandsEnabled));
		setDeviceIds((current) => merge(current, previous ? assignedDeviceIds(previous) : [], assignedDeviceIds(saved), sameDevices)
			.filter((id) => saved.devices.some((device) => device.deviceId === id && canAssign(device))));
		setReason((current) => preserveDraft ? current : "");
		setBlocked(false);
	}, []);
	const finish = useCallback((ticket) => {
		if (!alive.current || generation.current.value !== ticket) return;
		pending.current = false;
		setBusy(false);
		if (!sameScope()) {
			setSnapshot(null); setBlocked(true); setMessage("");
			setError("Support account or environment changed. Reload this page before continuing.");
		}
	}, [sameScope]);
	const reload = useCallback(async () => {
		if (pending.current || !sameScope()) return;
		const ticket = ++generation.current.value;
		pending.current = true;
		setBusy(true); setBlocked(true); setSnapshot(null); setError(""); setMessage("");
		try {
			if (!await authorize(ticket) || !isCurrent(ticket)) return;
			const response = await httpsCallable(functions, "getScervPrinterSetup")({ restaurantId });
			if (!isCurrent(ticket)) return;
			if (!validSnapshot(response.data, restaurantId)) throw new Error("Invalid setup response");
			applySnapshot(response.data, true);
		} catch (err) {
			if (isCurrent(ticket)) setError(errorMessage(err, false));
		} finally {
			finish(ticket);
		}
	}, [applySnapshot, authorize, finish, isCurrent, restaurantId, sameScope]);
	useEffect(() => {
		const counter = generation.current;
		alive.current = true;
		reload();
		return () => { alive.current = false; counter.value++; pending.current = false; };
	}, [reload]);

	const save = async (event) => {
		event.preventDefault();
		if (pending.current || blocked || !snapshot || !sameScope()) return;
		setError(""); setMessage("");
		if (!validModel(model)) { setError("Select a supported LAN printer model."); return; }
		if (!privateLan(identifier)) { setError("Enter a canonical private LAN IPv4 address, without a hostname, URL or port."); return; }
		if (!reason.trim() || reason.length > 240 || Array.from(reason).some((character) =>
			character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) {
			setError("Enter a support reason of 1 to 240 characters without control characters."); return;
		}
		const ticket = ++generation.current.value;
		pending.current = true; setBusy(true);
		try {
			if (!await authorize(ticket) || !isCurrent(ticket)) return;
			const response = await httpsCallable(functions, "saveScervPrinterSetup")({
				restaurantId, expectedRevision: snapshot.revision, identifier, model, receiptPrintingEnabled,
				drawerCommandsEnabled, deviceIds, reason: reason.trim(),
			});
			if (!isCurrent(ticket)) return;
			const result = response.data;
			if (result?.success !== true || typeof result.changed !== "boolean" || !validSnapshot(result, restaurantId) ||
				result.receiptPrinter?.identifier !== identifier || result.receiptPrinter.model !== model ||
				result.receiptPrintingEnabled !== receiptPrintingEnabled ||
				result.drawerCommandsEnabled !== drawerCommandsEnabled || !sameDevices(enabledDeviceIds(result), deviceIds) ||
				result.devices.some((device) => device.configEnabled && (!canAssign(device) || !device.drawerPrinterId || device.drawerIdentifier !== identifier))) {
				throw new Error("Unconfirmed setup response");
			}
			applySnapshot(result);
			setMessage(result.changed ? "Printer setup saved." : "Printer setup already matches. No changes saved.");
			// Only restaurant fields belong in the parent's profile form.
			onSaved?.({ receiptPrinter: result.receiptPrinter, receiptPrintingEnabled: result.receiptPrintingEnabled,
				drawerCommandsEnabled: result.drawerCommandsEnabled });
		} catch (err) {
			if (isCurrent(ticket)) { setBlocked(true); setError(errorMessage(err, true)); }
		} finally {
			finish(ticket);
		}
	};
	const disabled = busy || blocked;
	const dirty = snapshot && (model !== (snapshot.receiptPrinter?.model || "V-WRP2") ||
		identifier !== (snapshot.receiptPrinter?.identifier || "") || receiptPrintingEnabled !== snapshot.receiptPrintingEnabled ||
		drawerCommandsEnabled !== snapshot.drawerCommandsEnabled || !sameDevices(deviceIds, assignedDeviceIds(snapshot)) || reason !== "");
	const needsVolcoraAdapter = model === "V-WRP2" || snapshot?.receiptPrinter?.model === "V-WRP2";

	return <section className="receipt-printer-controls" aria-labelledby="receipt-printer-heading" aria-busy={busy}>
		<div className="receipt-printer-header">
			<h2 id="receipt-printer-heading">Printer setup</h2><span className="neutral-pill">{environment} only</span>
		</div>
		<dl>
			<dt>Restaurant</dt><dd>{restaurantId}</dd>
			<dt>Model</dt><dd>{snapshot ? snapshot.receiptPrinter ? `${modelLabel(snapshot.receiptPrinter.model)} / LAN` : "Not configured" : "Not loaded"}</dd>
			<dt>Configured IP</dt><dd>{snapshot ? snapshot.receiptPrinter?.identifier || "Not configured" : "Not loaded"}</dd>
			<dt>Saved activation</dt><dd>{snapshot ? `Receipts: ${snapshot.receiptPrintingEnabled ? "Enabled" : "Disabled"}; drawer commands: ${snapshot.drawerCommandsEnabled ? "Enabled" : "Disabled"}` : "Not loaded"}</dd>
			<dt>Automatic pickup slips</dt><dd>{typeof snapshot?.automaticPickupPrintingEnabled === "boolean" ? snapshot.automaticPickupPrintingEnabled ? "Enabled" : "Disabled" : "Not loaded"}</dd>
			<dt>Installed app support</dt><dd>Not verified from portal; enrollment does not confirm printer compatibility.</dd>
			<dt>Live connection</dt><dd>Not checked from portal</dd>
			<dt>Physical state</dt><dd>Unknown</dd>
		</dl>
		{error && <p className="restaurant-error" role="alert">{error}</p>}
		{message && <p className="restaurant-message" role="status">{message}</p>}
		{dirty && <p role="status">Unsaved printer changes.</p>}
		<form onSubmit={save} noValidate>
			<label htmlFor="receipt-printer-model">Printer model (LAN)</label>
			<select id="receipt-printer-model" value={model} disabled={disabled} required
				aria-describedby={needsVolcoraAdapter ? "receipt-printer-adapter-note" : undefined}
				onChange={(event) => setModel(event.target.value)}>
				{PRINTER_MODELS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
			</select>
			{needsVolcoraAdapter && <p id="receipt-printer-adapter-note" className="receipt-printer-adapter-note">
				Volcora adapter: new Android build required; older Star-only apps cannot print or open drawers for Volcora. Hardware validation pending.
			</p>}
			<label htmlFor="receipt-printer-address">Printer IP (private LAN IPv4)</label>
			<input id="receipt-printer-address" type="text" value={identifier} maxLength={15} autoComplete="off" spellCheck={false}
				required disabled={disabled} onChange={(event) => setIdentifier(event.target.value)} />
			<div className="receipt-printer-activation">
				<label className="receipt-printer-check">
					<input id="receipt-printer-receipts" type="checkbox" checked={receiptPrintingEnabled} disabled={disabled}
						onChange={(event) => setReceiptPrintingEnabled(event.target.checked)} />
					<span>Receipt printing enabled</span>
				</label>
				<label className="receipt-printer-check">
					<input id="receipt-printer-drawer" type="checkbox" checked={drawerCommandsEnabled} disabled={disabled}
						onChange={(event) => setDrawerCommandsEnabled(event.target.checked)} />
					<span>Drawer commands enabled</span>
				</label>
			</div>
			<fieldset className="receipt-printer-devices" disabled={disabled}>
				<legend>Drawer assignments</legend>
				{!snapshot ? <p>Devices not loaded.</p> : snapshot.devices.length === 0 ? <p>No enrolled devices.</p> :
					<ul>{snapshot.devices.map((device, index) => <li key={device.deviceId}>
						<label className="receipt-printer-check" htmlFor={`receipt-printer-device-${index}`}>
							<input id={`receipt-printer-device-${index}`} type="checkbox" checked={deviceIds.includes(device.deviceId)}
								disabled={disabled || !canAssign(device)} onChange={(event) => {
									const checked = event.target.checked;
									setDeviceIds((current) => checked ? [...current, device.deviceId] : current.filter((id) => id !== device.deviceId));
								}} />
							<span><strong>{device.label || device.deviceId}</strong><span className="receipt-printer-device-id">{device.deviceId}</span></span>
						</label>
						<div className="receipt-printer-device-details">
							<span>{device.revoked ? "Revoked" : device.eligible ? "Enrolled" : "Not eligible"}</span>
							<span>Saved target: {device.drawerIdentifier || "Not assigned"}{device.drawerPrinterId ? ` (${device.drawerPrinterId})` : ""}</span>
							<span>Saved association: {device.configEnabled ? "Enabled" : "Disabled"}</span>
						</div>
					</li>)}</ul>}
			</fieldset>
			<label htmlFor="receipt-printer-reason">Support reason (required)</label>
			<input id="receipt-printer-reason" type="text" value={reason} maxLength={240} autoComplete="off" required
				disabled={disabled} onChange={(event) => setReason(event.target.value)} />
			<div className="receipt-printer-actions">
				<button type="submit" disabled={disabled || !snapshot}>Save printer setup</button>
				<button type="button" disabled={busy} onClick={reload}>Refresh</button>
				{dirty && <button type="button" disabled={disabled} onClick={() => {
					applySnapshot(snapshot); setError(""); setMessage("");
				}}>Discard edits</button>}
			</div>
		</form>
		<h3>Recent hardware commands</h3>
		{!snapshot ? <p>Activity not loaded.</p> : snapshot.recentActivity.length === 0 ? <p>No recent command activity.</p> : <ul className="receipt-printer-activity">
			{snapshot.recentActivity.map((row) => {
				const date = activityDate(row.at);
				const device = snapshot.devices.find((entry) => entry.deviceId === row.deviceId);
				return <li key={row.id}>
					<strong className="receipt-printer-outcome">{stateLabel(row.kind)} | Outcome: {stateLabel(row.state)}</strong>
					<span>{row.deviceId ? `${device?.label || "Device"} (${row.deviceId})` : "Device unavailable"}</span>
					{date ? <time dateTime={date.toISOString()}>{date.toLocaleString()}</time> : <span>Time unavailable</span>}
				</li>;
			})}
		</ul>}
	</section>;
}

export default function ReceiptPrinterControls({ restaurantId, onSaved }) {
	const [user, loading] = useAuthState(auth);
	const environment = getStaffProvisioningEnvironment();
	if (!environment) return null;
	if (loading) return <p role="status">Checking support access...</p>;
	if (!user) return <p role="alert">Sign in with an authorized support account.</p>;
	return <PrinterForm key={`${environment}:${restaurantId}:${user.uid}`} restaurantId={restaurantId} user={user}
		environment={environment} onSaved={onSaved} />;
}
